import type { CustomerExternalEvent } from '@/services/conversations.api'
import { hasHtmlTags, sanitizeHtml } from '@/utils/sanitizeHtml'
import type { MantineColor } from '@mantine/core'
import {
  Badge,
  Box,
  Group,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
} from '@mantine/core'
import { IconBuildingStore, IconCalendarEvent, IconMessage, IconPhone } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'

const PANEL_WIDTH = 360
/** Vertical CRM dock strip width (fits badge + vertical label) */
const TAB_WIDTH = 52

const MESSAGE_BUBBLE_BLUE = '#3b82f6'

const CRM_DOCK_OPEN_STORAGE_KEY = 'automotive-dashboard:crm-activities-dock-open'

function readCrmDockOpenFromStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(CRM_DOCK_OPEN_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/** One row in the CRM activities timeline (derived from `CustomerExternalEvent`). */
interface CustomerTimelineActivity {
  id: string
  category: string
  occurredAt: string
  subject: string | null
  summary: string
  /** Raw CRM body when present (e.g. full Lead ADF); used for expand. */
  fullBody: string | null
  /** True when collapsed summary hides part of `fullBody`. */
  isExpandable: boolean
  direction?: string | null
  eventProvider: string
  integrationConnector: string
  providerLeadConsent?: 'granted' | 'not_granted' | 'missing'
}

interface CustomerExternalEventsDockProps {
  /** Embedded `customer.externalEvents` from `/customers/conversations`. */
  externalEvents?: CustomerExternalEvent[]
}

function formatActivityWhen(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function categoryColor(category: string): MantineColor {
  switch (category) {
    case 'COMMUNICATION_MESSAGE':
      return 'blue'
    case 'SALES_APPOINTMENT':
      return 'teal'
    case 'SERVICE_APPOINTMENT':
      return 'cyan'
    case 'SHOWROOM_VISIT':
      return 'violet'
    default:
      return 'gray'
  }
}

/** Trailing status on appointment subjects, e.g. "Sales appointment Current" → Current */
const APPOINTMENT_SUBJECT_STATUS_TAIL =
  /^(?<headline>.+?)\s+(?<status>current|canceled|cancelled|scheduled|completed|rescheduled|confirmed|pending|no[-\s]?show)$/i

function formatAppointmentStatusLabel(raw: string): string {
  return raw
    .replace(/[-_]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

function parseAppointmentSubject(subject: string): { headline: string; statusKey: string | null; statusLabel: string | null } {
  const m = subject.trim().match(APPOINTMENT_SUBJECT_STATUS_TAIL)
  if (!m?.groups?.headline || !m.groups.status) {
    return { headline: subject.trim(), statusKey: null, statusLabel: null }
  }
  const raw = m.groups.status
  const statusKey = raw.toLowerCase().replace(/[\s-]+/g, '')
  const statusLabel = formatAppointmentStatusLabel(raw)
  return { headline: m.groups.headline.trim(), statusKey, statusLabel }
}

function appointmentStatusBadgeColor(statusKey: string): MantineColor {
  switch (statusKey) {
    case 'canceled':
    case 'cancelled':
      return 'red'
    case 'completed':
    case 'noshow':
      return 'gray'
    case 'current':
    case 'confirmed':
      return 'green'
    case 'scheduled':
    case 'rescheduled':
    case 'pending':
      return 'yellow'
    default:
      return 'teal'
  }
}

function CategoryBulletIcon({ category }: { category: string }) {
  const color = categoryColor(category)
  const icon =
    category === 'COMMUNICATION_MESSAGE' ? (
      <IconMessage size={14} />
    ) : category === 'SALES_APPOINTMENT' || category === 'SERVICE_APPOINTMENT' ? (
      <IconCalendarEvent size={14} />
    ) : category === 'SHOWROOM_VISIT' ? (
      <IconBuildingStore size={14} />
    ) : (
      <IconPhone size={14} />
    )

  return (
    <ThemeIcon color={color} size={22} radius="xl" variant="filled">
      {icon}
    </ThemeIcon>
  )
}

const SUMMARY_MAX_CHARS = 280
const SUMMARY_TRUNCATE_AT = 277
/** Max height for an expanded CRM body inside the dock panel */
const EXPANDED_BODY_MAX_HEIGHT = 280

/**
 * Visible summary for CRM bodies that may contain HTML. Truncating the raw string
 * can split mid-tag; we sanitize then take textContent before capping length.
 */
function plainTextFromCrmBody(html: string): string {
  const safe = sanitizeHtml(html)
  const doc = new DOMParser().parseFromString(safe, 'text/html')
  return (doc.body.textContent || doc.body.innerText || '').replace(/\s+/g, ' ').trim()
}

function truncateSummaryPlain(text: string): string {
  if (text.length <= SUMMARY_MAX_CHARS) return text
  return `${text.slice(0, SUMMARY_TRUNCATE_AT)}…`
}

function summarizeExternalEvent(e: CustomerExternalEvent): string {
  const body = e.body?.trim()
  if (body) {
    if (body.length <= SUMMARY_MAX_CHARS) return body
    if (hasHtmlTags(body)) {
      return truncateSummaryPlain(plainTextFromCrmBody(body))
    }
    return `${body.slice(0, SUMMARY_TRUNCATE_AT)}…`
  }
  if (e.subject?.trim()) return e.subject.trim()
  return e.category.replace(/_/g, ' ')
}

function isActivityBodyExpandable(fullBody: string | null | undefined, summary: string): boolean {
  const body = fullBody?.trim()
  if (!body) return false
  if (body.length > SUMMARY_MAX_CHARS) return true
  if (hasHtmlTags(body)) {
    const plain = plainTextFromCrmBody(body)
    return plain.length > SUMMARY_MAX_CHARS || summary !== plain
  }
  return body !== summary
}

function apiEventsToTimelineRows(events: CustomerExternalEvent[]): CustomerTimelineActivity[] {
  return events
    .map((e): CustomerTimelineActivity => {
      const fullBody = e.body?.trim() ? e.body.trim() : null
      const summary = summarizeExternalEvent(e)
      return {
        id: e.id,
        category: e.category,
        occurredAt: e.occurredAt ?? e.ingestedAt,
        subject: e.subject,
        summary,
        fullBody,
        isExpandable: isActivityBodyExpandable(fullBody, summary),
        direction: e.direction,
        eventProvider: e.eventProvider,
        integrationConnector: e.integrationConnector,
        providerLeadConsent: e.providerLeadConsent,
      }
    })
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
}

export function CustomerExternalEventsDock({ externalEvents }: CustomerExternalEventsDockProps) {
  const [opened, setOpened] = useState(readCrmDockOpenFromStorage)
  const [expandedActivityIds, setExpandedActivityIds] = useState<Set<string>>(() => new Set())
  const computedColorScheme = useComputedColorScheme('light')

  const activities = useMemo(() => apiEventsToTimelineRows(externalEvents ?? []), [externalEvents])

  const expandableActivityIds = useMemo(
    () => activities.filter(activity => activity.isExpandable).map(activity => activity.id),
    [activities],
  )
  const hasExpandableActivities = expandableActivityIds.length > 0
  const allActivitiesExpanded = hasExpandableActivities && expandableActivityIds.every(id => expandedActivityIds.has(id))

  useEffect(() => {
    setExpandedActivityIds(new Set())
  }, [externalEvents])

  const eventCount = activities.length
  const tabDisabled = eventCount === 0
  /** Panel is visible only when the user preference is open and this customer has events */
  const panelExpanded = opened && !tabDisabled

  useEffect(() => {
    try {
      window.localStorage.setItem(CRM_DOCK_OPEN_STORAGE_KEY, String(opened))
    } catch {
      // ignore quota / private mode
    }
  }, [opened])

  const tabBg =
    computedColorScheme === 'dark'
      ? panelExpanded
        ? 'var(--mantine-color-dark-6)'
        : 'var(--mantine-color-dark-7)'
      : panelExpanded
        ? 'var(--mantine-color-blue-0)'
        : 'var(--mantine-color-gray-0)'

  const tabBorder = computedColorScheme === 'dark' ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'

  const handleExpandAllActivities = () => {
    setExpandedActivityIds(new Set(expandableActivityIds))
  }

  const handleCollapseAllActivities = () => {
    setExpandedActivityIds(new Set())
  }

  const handleToggleActivityExpanded = (activityId: string) => {
    setExpandedActivityIds(prev => {
      const next = new Set(prev)
      if (next.has(activityId)) {
        next.delete(activityId)
      } else {
        next.add(activityId)
      }
      return next
    })
  }

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        flexShrink: 0,
        alignItems: 'stretch',
      }}
    >
      {/* Sliding panel */}
      <Box
        style={{
          width: panelExpanded ? PANEL_WIDTH : 0,
          minWidth: 0,
          overflow: 'hidden',
          transition: 'width 200ms ease',
          borderLeft: panelExpanded ? `1px solid ${tabBorder}` : 'none',
          backgroundColor: computedColorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-body)',
        }}
      >
        <Box
          w={PANEL_WIDTH}
          h="100%"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            p="sm"
            style={{
              borderBottom: `1px solid ${tabBorder}`,
              flexShrink: 0,
            }}
          >
            <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
              <Text fw={600} size="sm">
                CRM Activities
              </Text>
              {hasExpandableActivities ? (
                <Group gap={6} wrap="nowrap">
                  {!allActivitiesExpanded ? (
                    <UnstyledButton onClick={handleExpandAllActivities}>
                      <Text size="xs" c="blue">
                        Expand all
                      </Text>
                    </UnstyledButton>
                  ) : null}
                  {expandedActivityIds.size > 0 ? (
                    <UnstyledButton onClick={handleCollapseAllActivities}>
                      <Text size="xs" c="dimmed">
                        Collapse all
                      </Text>
                    </UnstyledButton>
                  ) : null}
                </Group>
              ) : null}
            </Group>
          </Box>
          <ScrollArea style={{ flex: 1 }} type="auto" scrollbarSize={8} offsetScrollbars>
            <Box p="sm" pb="xl" pr="md">
              {activities.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No activities yet.
                </Text>
              ) : (
                <ActivitiesTimeline
                  activities={activities}
                  expandedActivityIds={expandedActivityIds}
                  onToggleExpanded={handleToggleActivityExpanded}
                />
              )}
            </Box>
          </ScrollArea>
        </Box>
      </Box>

      {/* Vertical tab — docked on the right; disabled when there are no CRM events */}
      <Tooltip
        label={
          tabDisabled
            ? 'No CRM activities for this customer'
            : panelExpanded
              ? 'Hide CRM activities'
              : `Show CRM activities (${eventCount})`
        }
        position="left"
        withArrow
      >
        <Box component="span" display="flex" h="100%" style={{ flexShrink: 0 }}>
          <UnstyledButton
            disabled={tabDisabled}
            onClick={() => setOpened(o => !o)}
            style={{
              width: TAB_WIDTH,
              minWidth: TAB_WIDTH,
              height: '100%',
              borderLeft: `1px solid ${tabBorder}`,
              backgroundColor: tabBg,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              paddingTop: 16,
              paddingBottom: 16,
              cursor: tabDisabled ? 'not-allowed' : 'pointer',
              opacity: tabDisabled ? 0.55 : 1,
            }}
          >
            <Badge
              size="lg"
              variant="filled"
              color={tabDisabled ? 'gray' : 'blue'}
              circle
              title={tabDisabled ? undefined : `${eventCount} CRM activities`}
              styles={{
                root: {
                  minWidth: 28,
                  height: 28,
                  paddingInline: eventCount > 99 ? 4 : 0,
                  fontSize: eventCount > 99 ? 10 : 12,
                  fontWeight: 700,
                  ...(!tabDisabled && {
                    backgroundColor: MESSAGE_BUBBLE_BLUE,
                    color: 'var(--mantine-color-white)',
                  }),
                },
              }}
            >
              {eventCount > 99 ? '99+' : eventCount}
            </Badge>
            <Text
              size="sm"
              fw={600}
              c={tabDisabled ? 'dimmed' : undefined}
              style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                letterSpacing: '0.08em',
                userSelect: 'none',
              }}
            >
              CRM Activities
            </Text>
          </UnstyledButton>
        </Box>
      </Tooltip>
    </Box>
  )
}

function ActivitiesTimeline({
  activities,
  expandedActivityIds,
  onToggleExpanded,
}: {
  activities: CustomerTimelineActivity[]
  expandedActivityIds: Set<string>
  onToggleExpanded: (activityId: string) => void
}) {
  return (
    <Timeline bulletSize={28} lineWidth={2} active={-1}>
      {activities.map(activity => (
        <Timeline.Item
          key={activity.id}
          bullet={<CategoryBulletIcon category={activity.category} />}
          title={<TimelineItemTitle activity={activity} />}
        >
          <Stack gap={6} mt={4}>
            <ExpandableActivitySummary
              activity={activity}
              expanded={expandedActivityIds.has(activity.id)}
              onToggle={() => onToggleExpanded(activity.id)}
            />
            <Text size="xs" c="dimmed">
              {formatActivityWhen(activity.occurredAt)}
            </Text>
          </Stack>
        </Timeline.Item>
      ))}
    </Timeline>
  )
}

function ExpandableActivitySummary({
  activity,
  expanded,
  onToggle,
}: {
  activity: CustomerTimelineActivity
  expanded: boolean
  onToggle: () => void
}) {
  if (!activity.isExpandable || !activity.fullBody) {
    return <TimelineRichSummary text={activity.summary} />
  }

  if (expanded) {
    return (
      <Stack gap={4}>
        <ScrollArea.Autosize mah={EXPANDED_BODY_MAX_HEIGHT} type="auto" scrollbarSize={6} offsetScrollbars>
          <Box
            component="pre"
            m={0}
            p="xs"
            style={{
              fontSize: 'var(--mantine-font-size-xs)',
              fontFamily: 'var(--mantine-font-family-monospace)',
              color: 'var(--mantine-color-dimmed)',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: 'var(--mantine-color-gray-0)',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
          >
            {activity.fullBody}
          </Box>
        </ScrollArea.Autosize>
        <UnstyledButton onClick={onToggle}>
          <Text size="xs" c="blue">
            Show less
          </Text>
        </UnstyledButton>
      </Stack>
    )
  }

  return (
    <Stack gap={4}>
      <TimelineRichSummary text={activity.summary} />
      <UnstyledButton onClick={onToggle}>
        <Text size="xs" c="blue">
          Show more
        </Text>
      </UnstyledButton>
    </Stack>
  )
}

function TimelineRichSummary({ text }: { text: string }) {
  if (hasHtmlTags(text)) {
    return (
      <Box
        component="div"
        style={{
          fontSize: 'var(--mantine-font-size-sm)',
          color: 'var(--mantine-color-dimmed)',
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
      />
    )
  }
  return (
    <Text size="sm" c="dimmed" lineClamp={5}>
      {text}
    </Text>
  )
}

function TimelineRichSubject({ text }: { text: string }) {
  if (hasHtmlTags(text)) {
    return (
      <Box
        component="div"
        style={{
          fontSize: 'var(--mantine-font-size-sm)',
          fontWeight: 600,
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
      />
    )
  }
  return (
    <Text size="sm" fw={600} lh={1.35}>
      {text}
    </Text>
  )
}

function ProviderLeadConsentBadge({ status }: { status: 'granted' | 'not_granted' | 'missing' }) {
  if (status === 'granted') {
    return (
      <Badge size="xs" variant="light" color="green">
        Accepted
      </Badge>
    )
  }
  if (status === 'not_granted') {
    return (
      <Badge size="xs" variant="light" color="red">
        Not Accepted
      </Badge>
    )
  }
  return (
    <Badge size="xs" variant="light" color="gray">
      Missing
    </Badge>
  )
}

function TimelineCommunicationTypeLine({
  subject,
  direction,
  providerLeadConsent,
}: {
  subject: string
  direction: string | null | undefined
  providerLeadConsent?: 'granted' | 'not_granted' | 'missing'
}) {
  const dir = direction?.trim()
  const dirColor = dir === 'Incoming' ? 'green' : 'orange'

  if (hasHtmlTags(subject)) {
    return (
      <Group gap={6} align="flex-start" wrap="wrap">
        <Box
          component="span"
          style={{
            fontSize: 'var(--mantine-font-size-sm)',
            fontWeight: 600,
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(subject) }}
        />
        {dir ? (
          <>
            <Text component="span" fw={600} lh={1.35}>
              :
            </Text>
            <Badge size="xs" variant="outline" color={dirColor}>
              {dir}
            </Badge>
          </>
        ) : null}
        {providerLeadConsent ? <ProviderLeadConsentBadge status={providerLeadConsent} /> : null}
      </Group>
    )
  }

  return (
    <Group gap={4} align="center" wrap="wrap">
      <Text component="span" size="sm" fw={600} lh={1.35}>
        {subject}
      </Text>
      {dir ? (
        <>
          <Text component="span" size="sm" fw={600} lh={1.35}>
            :
          </Text>
          <Badge size="xs" variant="outline" color={dirColor}>
            {dir}
          </Badge>
        </>
      ) : null}
      {providerLeadConsent ? <ProviderLeadConsentBadge status={providerLeadConsent} /> : null}
    </Group>
  )
}

function TimelineAppointmentHeadline({ activity }: { activity: CustomerTimelineActivity }) {
  if (!activity.subject) return null
  const parsed = parseAppointmentSubject(activity.subject)
  if (!parsed.statusKey) {
    return <TimelineRichSubject text={activity.subject} />
  }
  return (
    <Group gap={8} align="center" wrap="wrap">
      <TimelineRichSubject text={parsed.headline} />
      <Badge size="xs" variant="light" color={appointmentStatusBadgeColor(parsed.statusKey)}>
        {parsed.statusLabel}
      </Badge>
    </Group>
  )
}

function TimelineItemTitle({ activity }: { activity: CustomerTimelineActivity }) {
  const isComm = activity.category === 'COMMUNICATION_MESSAGE'
  const isAppointment = activity.category === 'SALES_APPOINTMENT' || activity.category === 'SERVICE_APPOINTMENT'

  return (
    <Stack gap={6}>
      <Group gap={6} wrap="wrap">
        <Badge size="xs" variant="light" color={categoryColor(activity.category)}>
          {activity.category.replace(/_/g, ' ')}
        </Badge>
        {activity.direction && !isComm ? (
          <Badge size="xs" variant="outline" color={activity.direction === 'Incoming' ? 'green' : 'orange'}>
            {activity.direction}
          </Badge>
        ) : null}
      </Group>
      {isComm && activity.subject ? (
        <TimelineCommunicationTypeLine
          subject={activity.subject}
          direction={activity.direction}
          providerLeadConsent={activity.providerLeadConsent}
        />
      ) : isAppointment ? (
        <TimelineAppointmentHeadline activity={activity} />
      ) : activity.subject ? (
        <TimelineRichSubject text={activity.subject} />
      ) : null}
      <Text size="xs" c="dimmed">
        {activity.eventProvider}
        {activity.integrationConnector ? ` · ${activity.integrationConnector}` : ''}
      </Text>
    </Stack>
  )
}
